import i18n from "@/i18n"
import { useAuthStore } from "@/features/auth/useAuthStore"
import { requestApproval } from "./approval" // jarvis-r-a

interface CodedDetail {
  code: string
  params?: Record<string, unknown>
}

function isCoded(detail: unknown): detail is CodedDetail {
  return typeof detail === "object" && detail !== null && "code" in detail && typeof (detail as CodedDetail).code === "string"
}

export function buildErrorMessage(body: { detail?: unknown }, status: number): string {
  if (isCoded(body.detail)) {
    const { code, params } = body.detail
    const key = `errors:${code}`
    const translated = i18n.t(key, { ...(params ?? {}), defaultValue: code })
    return translated
  }
  if (typeof body.detail === "string") return body.detail
  return `HTTP ${status}`
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = useAuthStore.getState().token
  const isFormData = init?.body instanceof FormData
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...init?.headers,
    },
  })

  // jarvis-r-a: schuetzende Aenderung braucht Einzelfreigabe -> Bestaetigungsfenster, dann identisch wiederholen
  if (res.status === 428 && !(init?.headers && "X-Approval-Id" in (init.headers as Record<string, string>))) {
    const b = (await res.clone().json().catch(() => ({}))) as {
      detail?: { code?: string; params?: { approval_id?: string } }
    }
    const aid = b.detail?.params?.approval_id
    if (b.detail?.code === "approval_required" && aid) {
      const ok = await requestApproval({ kind: "approval", approvalId: aid, method: init?.method ?? "GET", path })
      if (!ok) {
        const err = new Error("Действие не подтверждено") as Error & { status?: number }
        err.status = 428
        throw err
      }
      return request<T>(path, {
        ...init,
        headers: { ...(init?.headers as Record<string, string> | undefined), "X-Approval-Id": aid },
      })
    }
  }
  if (res.status === 401 && path !== "/auth/login") { // jarvis-creds
    const b401 = (await res.clone().json().catch(() => ({}))) as { detail?: { code?: string } }
    if (b401.detail?.code === "session_idle_timeout") {
      try {
        sessionStorage.setItem("hh-idle-logout", "1") // jarvis-r-a
      } catch {
        /* ignore */
      }
    }
    useAuthStore.getState().logout()
    throw new Error(i18n.t("errors:not_authenticated", { defaultValue: "Not authenticated" }))
  }
  if (res.status === 403) {
    const b403 = (await res.clone().json().catch(() => ({}))) as { detail?: { code?: string } }
    if (b403.detail?.code === "credentials_change_required" || b403.detail?.code === "approval_pending") { // jarvis-register
      useAuthStore.getState().logout()
    }
  }
  if (res.status === 204) return undefined as T
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    const err = new Error(buildErrorMessage(body, res.status)) as Error & { status?: number }
    err.status = res.status
    throw err
  }
  return res.json()
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  postForm: <T>(path: string, form: FormData) =>
    request<T>(path, { method: "POST", body: form }),
}
